import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { useGetList } from 'react-admin'
import { GemeenteResponse } from '../../types/apiRequests'

interface Props {
  value: GemeenteResponse | null
  onChange: (gem: GemeenteResponse | null) => void
  label?: string
  size?: 'small' | 'medium'
  sx?: object
}

const GemeenteAutocomplete: FC<Props> = ({
  value,
  onChange,
  label = 'Gemeente',
  size = 'small',
  sx,
}) => {
  const [search, setSearch] = useState('')
  const { data: gemeenten = [], isLoading } = useGetList<GemeenteResponse>('gemeenten', {
    sort: { field: 'naam', order: 'ASC' },
    pagination: { page: 1, perPage: 50 },
    filter: search ? { q: search } : {},
  })

  // If the selected gemeente isn't in the current search page (e.g. after
  // refresh-restore of a non-alphabetically-early gemeente), prepend it so
  // MUI Autocomplete can render its label instead of going blank.
  const options = useMemo(() => {
    if (!value) return gemeenten
    if (gemeenten.some((g) => g.gm_code === value.gm_code)) return gemeenten
    return [value, ...gemeenten]
  }, [gemeenten, value])

  return (
    <Autocomplete
      size={size}
      options={options}
      getOptionLabel={(option) => `${option.naam} (${option.gm_code})`}
      loading={isLoading}
      value={value}
      onInputChange={(_e, v) => setSearch(v)}
      onChange={(_e, v) => onChange(v)}
      isOptionEqualToValue={(option, candidate) => option.gm_code === candidate.gm_code}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default GemeenteAutocomplete
